import { z } from 'zod';

import type { RunProductsParams } from '../services/curation.service';

import { isReviewReason, type ReviewReason } from './enrichment-review';
import { ITEM_STATUSES, PRODUCT_DISPOSITIONS, RUN_LINEAGE_STAGES } from './lineage-images-search';

/** One product's listings the Listings view narrows to (a product rarely has more than a few). */
const MAX_LISTING_IDS = 50;
/** Spot-check ids carried in the URL (the decision panel sends 5). */
const MAX_SPOT_IDS = 50;

/** Sort choices the run-products table offers (the server sorts by title | brand | category | sku). */
export const RUN_PRODUCTS_SORTS = ['title-asc', 'title-desc', 'brand', 'category', 'sku'] as const;

/** Page sizes offered on the run-products tables (server max is 100). */
export const RUN_PRODUCTS_PAGE_SIZES = [20, 50, 100];

/** Rows per page when the URL does not say. */
export const DEFAULT_PAGE_SIZE = 20;

/**
 * URL search params of /catalog-v2/runs/$runId/products, so filters, sort, page and
 * the active tab survive Back. Every field is optional (links need not pass any) and
 * falls back to its default on a bad value.
 */
export const runProductsSearchSchema = z.object({
  // `listings` is the per-listing × stage audit grid folded in from the former Lineage tab.
  view: z.enum(['products', 'skipped', 'listings']).optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  attention: z.enum(['all', 'flagged', 'clean']).optional().catch(undefined),
  sort: z.enum(RUN_PRODUCTS_SORTS).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  size: z.number().int().min(1).max(100).optional().catch(undefined),
  skippedPage: z.number().int().min(1).optional().catch(undefined),
  // The Listings view's own filters (ex-Lineage tab URL state). INSIGHTS isn't a lineage-ledger
  // stage (the manual Stage ▾ picker never offers it) but the Overview's INSIGHTS row drills in
  // with it per the approved design, so it's accepted as a deep-link value alongside the rest.
  stage: z
    .union([z.enum(RUN_LINEAGE_STAGES), z.literal('INSIGHTS')])
    .optional()
    .catch(undefined),
  status: z.enum(ITEM_STATUSES).optional().catch(undefined),
  disposition: z.enum(PRODUCT_DISPOSITIONS).optional().catch(undefined),
  /** Opens one listing's preview in the Listings view — the product page's "Open in batch ↗". */
  listing: z.string().optional().catch(undefined),
  /** Opens the journey drawer for this feed product on arrival (any view). */
  feedProductId: z.string().optional().catch(undefined),
  /** The Listings view narrowed to one product's listings — a product row's "N listings" badge. */
  listingIds: z.array(z.string()).max(MAX_LISTING_IDS).optional().catch(undefined),
  /** Design B — "⚑ Needs your review": the Content generation review list (the panel's drills). */
  lens: z.enum(['review']).optional().catch(undefined),
  /** Lens filters, each a removable chip: a field key, a review reason, a server tier, a type. */
  field: z.string().optional().catch(undefined),
  reason: z
    .custom<ReviewReason | 'any'>((v) => v === 'any' || isReviewReason(v))
    .optional()
    .catch(undefined),
  // NEEDS_LOOK = REVIEW or FAIL — the "⚑ Needs your review" segment's own rule (the verdict's).
  tier: z.enum(['PASS', 'REVIEW', 'FAIL', 'NEEDS_LOOK']).optional().catch(undefined),
  type: z.string().optional().catch(undefined),
  /** The decision panel's "Spot-check N" — the seeded sample of products that look fine. */
  spot: z.array(z.string()).max(MAX_SPOT_IDS).optional().catch(undefined),
  /** Lens focus mode on one product — the old `/gate?productId=` deep link lands here. */
  focus: z.string().optional().catch(undefined),
  /** Option 2 — the gate the decision panel shows (a stage code); absent = the open gate. */
  decision: z.string().min(1).optional().catch(undefined),
  /** Option 2 — the decision panel's state; absent = expanded (collapsed on small windows). */
  panel: z.enum(['collapsed', 'expanded']).optional().catch(undefined),
});

/** The lens's URL-held filters (the rest of the review toolbar stays page state). */
export type ReviewLensSearch = Pick<
  RunProductsSearch,
  'field' | 'reason' | 'tier' | 'type' | 'spot'
>;

/** Lens search for one drill — every other lens filter cleared, so a drill never stacks. */
export function reviewLensSearch(patch: ReviewLensSearch = {}): Partial<RunProductsSearch> {
  return {
    lens: 'review',
    field: patch.field,
    reason: patch.reason,
    tier: patch.tier,
    type: patch.type,
    spot: patch.spot,
    focus: undefined,
    attention: undefined,
    page: undefined,
    view: undefined,
  };
}

/** Parsed run-products search params. */
export type RunProductsSearch = z.infer<typeof runProductsSearchSchema>;

/**
 * URL search → GET curation-runs/{runId}/products params. The page's summary and the
 * table both build params here, so they share one query cache entry. Membership is the server's
 * default `source=auto`: an old run lists rows a later run took over (`still_current: false`).
 */
export function toRunProductsParams(search: RunProductsSearch): RunProductsParams {
  const attention = search.attention ?? 'all';
  return {
    page: search.page ?? 1,
    size: search.size ?? DEFAULT_PAGE_SIZE,
    // The table renders list-row keys only (§50; ignored by older backends).
    fields: 'summary',
    ...(search.q ? { search: search.q } : {}),
    ...(attention === 'all' ? {} : { needsAttention: attention === 'flagged' }),
    ...toSortParams(search.sort),
  };
}

/** Sort choice → the server's `sortBy` / `sortDir` pair. */
function toSortParams(
  sort: RunProductsSearch['sort'],
): Pick<RunProductsParams, 'sortBy' | 'sortDir'> {
  switch (sort) {
    case 'title-asc':
      return { sortBy: 'title', sortDir: 'asc' };
    case 'title-desc':
      return { sortBy: 'title', sortDir: 'desc' };
    case 'brand':
    case 'category':
    case 'sku':
      return { sortBy: sort, sortDir: 'asc' };
    default:
      return {};
  }
}
